import { useState, useEffect, useCallback } from 'react';
import loginApi from 'api/requests/login';
import paths from 'routes/paths';

const SDK_SRC = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/pt_BR/appleid.auth.js';
const PENDING_NAME_KEY = 'apple_pending_name';
const CANCELLED_BY_USER = 'popup_closed_by_user';

// O nonce emitido pela API vale 10 minutos e é de uso único. Renovamos antes
// disso para que uma aba aberta há bastante tempo não caia em 401 no clique.
const NONCE_REFRESH_MS = 8 * 60 * 1000;

// O Sign in with Apple não tem pacote npm com provider React como o Google: é um
// script global. A promessa em nível de módulo garante uma carga só, mesmo com o
// botão montado na tela /entrar e no LoginModal ao mesmo tempo.
let sdkPromise = null;

function loadSdk() {
    if (window.AppleID) {
        return Promise.resolve();
    }

    if (!sdkPromise) {
        sdkPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = SDK_SRC;
            script.async = true;
            script.onload = resolve;
            script.onerror = () => {
                sdkPromise = null;
                reject(new Error('Não foi possível carregar o login da Apple.'));
            };
            document.head.appendChild(script);
        });
    }

    return sdkPromise;
}

// A Apple manda o nome do usuário uma única vez, na primeira autorização, e nunca
// mais — nem em novo login. Se o POST falhar logo depois, o nome se perde de vez e
// a conta nasce como "Usuario Apple"; a única saída seria o usuário revogar o
// acesso nos Ajustes. Guardar antes de enviar deixa o nome disponível no retry.
function rememberName(name) {
    try {
        sessionStorage.setItem(PENDING_NAME_KEY, JSON.stringify(name));
    } catch (error) {
        // Navegação privada pode recusar a escrita. O login segue; só o retry perde o nome.
    }
}

function recallName() {
    try {
        return JSON.parse(sessionStorage.getItem(PENDING_NAME_KEY));
    } catch (error) {
        return null;
    }
}

function forgetName() {
    try {
        sessionStorage.removeItem(PENDING_NAME_KEY);
    } catch (error) {
        // idem
    }
}

/**
 * Login com Apple na web.
 *
 * O SDK é carregado e inicializado **antes** do clique de propósito: o popup da
 * Apple só escapa do bloqueador se abrir no mesmo tick do gesto do usuário, e
 * qualquer `await` antes de `signIn()` gasta esse gesto. Por isso `signIn` não
 * faz rede nenhuma — busca de nonce e `init` acontecem em `prepare`.
 *
 * `signIn()` resolve em `null` quando o usuário fecha o popup: é cancelamento,
 * não erro, e não deve virar mensagem na tela.
 *
 * @param {boolean} enabled - false não carrega nada (ex.: modal fechado)
 */
export default function useAppleSignIn(enabled = true) {
    const [ready, setReady] = useState(false);

    const prepare = useCallback(async () => {
        const clientId = import.meta.env.VITE_APPLE_SERVICES_ID;

        if (!clientId) {
            return;
        }

        try {
            await loadSdk();

            const { data } = await loginApi.appleNonce();
            const clientUrl = String(import.meta.env.VITE_CLIENT_URL).replace(/\/$/, '');

            window.AppleID.auth.init({
                clientId,
                scope: 'name email',
                redirectURI: `${clientUrl}${paths.login}`,
                usePopup: true,
                nonce: data.nonce
            });

            setReady(true);
        } catch (error) {
            setReady(false);
        }
    }, []);

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }

        prepare();

        const intervalId = setInterval(prepare, NONCE_REFRESH_MS);

        return () => clearInterval(intervalId);
    }, [enabled, prepare]);

    const signIn = useCallback(async () => {
        if (!ready) {
            throw new Error('Login com Apple indisponível no momento.');
        }

        let response;

        try {
            response = await window.AppleID.auth.signIn();
        } catch (error) {
            if (error?.error === CANCELLED_BY_USER) {
                return null;
            }

            throw new Error('Erro ao entrar com a Apple. Tente novamente.');
        }

        if (response?.user?.name) {
            rememberName(response.user.name);
        }

        const pendingName = recallName();

        return {
            identity_token: response.authorization.id_token,
            first_name: pendingName?.firstName,
            last_name: pendingName?.lastName
        };
    }, [ready]);

    return { ready, signIn, prepare, confirmNameDelivered: forgetName };
}
